import { Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { TranslocoDirective } from '@jsverse/transloco';
import { AuthService } from '../../../core/auth/auth.service';
import { UserService } from '../../../core/user/user.service';
import { User } from '../../../core/user/user.types';
import { FidelityBasePageComponent } from '../../../shared/components/fidelity-base-page/fidelity-base-page.component';
import { FidelityOverlayPageWrapperComponent } from '../../../shared/components/fidelity-overlay-page-wrapper/fidelity-overlay-page-wrapper.component';
import { TbItemsListComponent } from '../../../shared/components/tb-options-list/tb-items-list.component';
import { TbConfirmationService } from '../../../shared/services/tb-confirmation/tb-confirmation.service';
import { TbConfirmationConfig } from '../../../shared/services/tb-confirmation/tb-confirmation.types';
import { TbListItem } from '../fidelity-app.types';
import { AccountFiscalInfoPageComponent } from './account-fiscal-info/account-fiscal-info-page.component';
import { AccountUserInfoPageComponent } from './account-user-info/account-user-info-page.component';
import { ConsentsPageComponent } from './consents/consents-page.component';
import { DeleteAccountPageComponent } from './delete-account/delete-account-page.component';
import { SecurityPageComponent } from './security/security-page.component';

@Component({
    selector: 'app-account-component',
    imports: [
        MatIcon,
        MatCard,
        MatCardContent,
        TbItemsListComponent,
        FidelityOverlayPageWrapperComponent,
        TranslocoDirective,
    ],
    templateUrl: './account-page.component.html',
    styleUrl: './account-page.component.scss',
})
export class AccountPageComponent extends FidelityBasePageComponent {
    currentUser: User = null;
    availablePages = {};
    accountOptions: TbListItem[];

    constructor(
        private tbConfirmationService: TbConfirmationService,
        private authService: AuthService,
        private userService: UserService
    ) {
        super();

        this.userService.user$.pipe(takeUntilDestroyed()).subscribe((user) => {
            this.currentUser = user;
        });

        this.accountOptions = [
            {
                icon: 'heroicons_outline:user',
                title: this.translocoService.translate('fidelity-app.account.your-info-title'),
                subtitle: this.translocoService.translate('fidelity-app.account.your-info-subtitle'),
                linkedPage: AccountUserInfoPageComponent,
            },
            {
                icon: 'mat_outline:receipt_long',
                title: this.translocoService.translate('fidelity-app.account.billing-title'),
                subtitle: this.translocoService.translate('fidelity-app.account.billing-subtitle'),
                linkedPage: AccountFiscalInfoPageComponent,
            },
            {
                icon: 'heroicons_outline:lock-closed',
                title: this.translocoService.translate('fidelity-app.account.security-title'),
                subtitle: this.translocoService.translate('fidelity-app.account.security-subtitle'),
                linkedPage: SecurityPageComponent,
            },
            {
                icon: 'heroicons_outline:check',
                title: this.translocoService.translate('fidelity-app.account.consent-title'),
                subtitle: this.translocoService.translate('fidelity-app.account.consent-subtitle'),
                linkedPage: ConsentsPageComponent,
            },
            {
                icon: 'heroicons_outline:trash',
                title: this.translocoService.translate('fidelity-app.account.delete-account-title'),
                subtitle: this.translocoService.translate('fidelity-app.account.delete-account-subtitle'),
                linkedPage: DeleteAccountPageComponent,
                dangerousOp: true,
            },
            {
                icon: 'heroicons_outline:arrow-right-on-rectangle',
                title: this.translocoService.translate('fidelity-app.account.logout-title'),
                hideChevron: true,
                onClick: this.logout,
                alignBottom: true,
            },
        ];
    }

    logout: Function = () => {
        const dialogConfig: TbConfirmationConfig = {
            title: this.translocoService.translate('fidelity-app.account.logout.title'),
            message: this.translocoService.translate('fidelity-app.account.logout.description'),
            icon: {
                color: 'accent',
            },
            dismissible: false,
            actions: {
                cancel: {
                    show: true,
                    label: this.translocoService.translate('generic.no'),
                },
                confirm: {
                    show: true,
                    label: this.translocoService.translate('generic.yes'),
                    color: 'accent',
                },
            },
            panelCass: this.fidelityAppNavService.deviceIsMobile ? 'mobile' : 'default',
        };
        const dialogRef = this.tbConfirmationService.open(dialogConfig);

        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'confirmed') {
                this.authService
                    .signOut()
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => {
                        this.notifyResult(this.translocoService.translate('fidelity-app.account.logout-success'));
                        this.fidelityAppNavService.closePageAndClearHistory();
                    });
            }
        });
    };
}
